// ═══════════════════════════════════════════════════════════════
// DonorDetailView — Communication Section
// Path: src/components/donorDetail/CommunicationSection.jsx
//
// Extracted from DonorDetailView.jsx during modularization Phase 3.
// Contains the contact log list with type icons, count header,
// Log Contact button, and per-entry delete button.
//
// Props-only — does not call useApp() directly.
// ═══════════════════════════════════════════════════════════════

import React from "react";
import { MessageSquare, Pencil, X } from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { CONTACT_LOG_TYPES, CONTACT_PURPOSES } from "../../constants/contactTypes";
import { ActionButton } from "../ui/ActionButton";
import { ContactLogIcon } from "../donors/DonorParts";

// ═══════════════════════════════════════════════════════════════
// CommunicationContent
// ═══════════════════════════════════════════════════════════════

export const CommunicationContent = ({
  contactLog,
  canLogContact,
  onLogContact,
  onEditLog,
  onDeleteLog,
  palette,
}) => (
  <>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 16,
      }}
    >
      <span style={{ fontSize: FONT_SIZES.sm, color: palette.text2 }}>
        {contactLog.length} contact
        {contactLog.length !== 1 ? "s" : ""} logged
      </span>
      {canLogContact && (
        <ActionButton
          label="Log Contact"
          icon={Pencil}
          small
          onClick={onLogContact}
        />
      )}
    </div>
    {contactLog.length === 0 ? (
      <div style={{ textAlign: "center", padding: "20px 0" }}>
        <MessageSquare
          size={ICON_SIZES.xl}
          style={{ color: palette.text3, marginBottom: 8 }}
        />
        <p style={{ color: palette.text3, fontSize: FONT_SIZES.sm, margin: 0 }}>
          No contacts logged yet. Start tracking your engagement.
        </p>
      </div>
    ) : (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          maxHeight: 320,
          overflowY: "auto",
        }}
      >
        {contactLog.map((entry) => {
          const typeInfo =
            CONTACT_LOG_TYPES.find((t) => t.id === entry.type) ||
            CONTACT_LOG_TYPES[0];
          const date = new Date(entry.date || entry.createdAt);
          const dateStr = date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
          const timeStr = date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
          });
          return (
            <div
              key={entry.id}
              style={{
                display: "flex",
                gap: 12,
                padding: "12px 14px",
                borderRadius: 8,
                background: palette.inputBg,
                border: `1px solid ${palette.cardBorder}`,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  flexShrink: 0,
                  background: `${typeInfo.color}12`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ContactLogIcon type={entry.type} size={ICON_SIZES.md} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 8,
                  }}
                >
                  <div>
                    <span
                      style={{
                        fontSize: FONT_SIZES.sm,
                        fontWeight: 600,
                        color: typeInfo.color,
                      }}
                    >
                      {typeInfo.name}
                    </span>
                    {entry.subject && (
                      <span
                        style={{
                          fontSize: FONT_SIZES.sm,
                          color: palette.text1,
                          marginLeft: 8,
                        }}
                      >
                        {entry.subject}
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flexShrink: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: FONT_SIZES.xs,
                        color: palette.text3,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {dateStr} {timeStr}
                    </span>
                    {canLogContact && onEditLog && (
                      <button
                        onClick={() => onEditLog(entry)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: palette.text3,
                          padding: 2,
                          display: "flex",
                        }}
                        title="Edit log entry"
                      >
                        <Pencil size={ICON_SIZES.xs} />
                      </button>
                    )}
                    <button
                      onClick={() => onDeleteLog(entry.id)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: palette.text3,
                        padding: 2,
                        display: "flex",
                      }}
                      title="Delete log entry"
                    >
                      <X size={ICON_SIZES.xs} />
                    </button>
                  </div>
                </div>
                {entry.purpose && entry.purpose.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      gap: 4,
                      flexWrap: "wrap",
                      marginTop: 4,
                    }}
                  >
                    {entry.purpose.map((pid) => {
                      const cp = CONTACT_PURPOSES.find((p) => p.id === pid);
                      if (!cp) return null;
                      return (
                        <span
                          key={pid}
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            padding: "2px 8px",
                            borderRadius: 6,
                            background: `${cp.color}15`,
                            color: cp.color,
                            fontFamily: FONT_FAMILY,
                          }}
                        >
                          {cp.name}
                        </span>
                      );
                    })}
                  </div>
                )}
                {entry.notes && (
                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: FONT_SIZES.xs,
                      color: palette.text2,
                      lineHeight: 1.4,
                    }}
                  >
                    {entry.notes}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    )}
  </>
);
